// Phase 197 — Cash flow forecast pure function.
//
// Predicts the next N days' balance based on active recurring rules.
// Each rule fires on its `day_of_month` per month, clamped to the last
// day of the month (so a day=28 rule fires on Feb 28 in a 28-day Feb),
// within its `effective_from`..`effective_to` (inclusive) range.
//
// Function is pure: same inputs → same outputs, no DB, no time-of-day
// leakage. The caller passes `today` explicitly so tests are deterministic.
//
// Shape: returns one point per day for `days + 1` points (point 0 is the
// current state, points 1..days are the forecast). This shape lets the
// chart draw a "today" line + the projection without re-computing
// offsets at the call site.

/**
 * @param {Object} input
 * @param {Array<{type: 'income'|'expense', amount: number, day_of_month: number, effective_from?: string|null, effective_to?: string|null, active?: boolean, note?: string}>} input.rules
 * @param {Date} input.today
 * @param {number} input.currentBalance
 * @param {number} [input.days=30]
 * @returns {Array<{iso: string, label: string, balance: number, delta: number, events: Array<{type: string, amount: number, note: string}>}>}
 */
export function computeCashFlowForecast({ rules = [], today, currentBalance = 0, days = 30 } = {}) {
  if (!(today instanceof Date) || Number.isNaN(today.getTime())) {
    throw new Error('forecast: today must be a valid Date');
  }
  const points = [];
  let running = currentBalance;
  for (let i = 0; i <= days; i++) {
    const date = addDays(today, i);
    const dateYm = ym(date);
    let delta = 0;
    const events = [];
    if (i > 0) {
      // Only project future days — point 0 is the "current state" snapshot.
      for (const r of rules) {
        if (r.active === false) continue;
        if (r.effective_from && dateYm < r.effective_from) continue;
        if (r.effective_to && dateYm > r.effective_to) continue;
        const ed = effectiveDay(r, date);
        if (ed === null) continue; // day_of_month exceeds this month's length
        if (ed !== date.getDate()) continue;
        const amt = Number(r.amount) || 0;
        if (amt <= 0) continue;
        const signed = r.type === 'expense' ? -amt : amt;
        delta += signed;
        events.push({ type: r.type, amount: amt, note: r.note || '' });
      }
      running += delta;
    }
    points.push({
      iso: toIsoDate(date),
      label: `${date.getMonth() + 1}/${date.getDate()}`,
      balance: running,
      delta,
      events,
    });
  }
  return points;
}

// ── helpers (also exported for tests + reuse) ──────────────────────────────

export function ym(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

export function lastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

// Clamp rule.day_of_month to the last day of `date`'s month, so a rule
// defined as day=28 fires on Feb 28 (in a 28-day Feb) without spilling
// into March.
//
// IMPORTANT: if `rule.day_of_month` exceeds the last day of this month
// (e.g. day=31 in a 30-day month, or day=29 in a 28-day Feb), this rule
// simply does NOT fire this month. We return null so the caller can
// skip it. The rule will fire next month if the month has enough days.
//
// Returns: the day-of-month this rule should fire on, or null to skip.
export function effectiveDay(rule, date) {
  const last = lastDayOfMonth(date);
  if (rule.day_of_month > last) return null;
  return Math.min(rule.day_of_month, last);
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}